import type { Book } from '@/mocks/books';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../shared/book-publication-status';

interface BookCardProps {
  book: Book;
}

export default function BookCard({ book }: BookCardProps) {
  const publicationBadge = getBookPublicationBadge(book.publishDate);
  return (
    <article className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden transition-colors hover:border-secondary-300">
      <div className="p-5 md:p-6 flex flex-col h-full">
        <div className="flex gap-5 mb-4">
          {/* Cover image */}
          <div className="relative w-20 md:w-24 h-28 md:h-32 flex-shrink-0 rounded-sm flex items-center justify-center overflow-hidden bg-background-100">
            {book.coverImage ? (
              <img
                src={book.coverImage}
                alt={book.title}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="text-center px-2">
                <i className="ri-book-2-line text-2xl text-background-400 block mb-1"></i>
                <span className="text-[10px] text-background-400 font-label">待匯入</span>
              </div>
            )}
            {publicationBadge && (
              <span className={`absolute top-1 left-1 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${getBookPublicationBadgeClass(publicationBadge)}`}>
                {getBookPublicationBadgeLabel(publicationBadge)}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 leading-snug mb-1">
              {book.title}
            </h3>
            {book.subtitle && (
              <p className="text-sm text-foreground-500 mb-2">{book.subtitle}</p>
            )}
            <div className="text-sm text-foreground-600 space-y-0.5">
              <p>作者：{book.author}</p>
              {book.category && <p>分類：{book.category}</p>}
            </div>
          </div>
        </div>

        <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1">{book.description}</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {book.suitableReaders && (
            <span className="text-xs px-2.5 py-1 bg-secondary-50 text-secondary-700 rounded-full font-label whitespace-nowrap">
              {book.suitableReaders.length > 30 ? book.suitableReaders.slice(0, 30) + '...' : book.suitableReaders}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {book.tags.map((tag) => (
            <span key={tag} className="text-[11px] text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-background-200">
          <a
            href="https://catalog.sonbook.net"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap flex items-center gap-1"
          >
            前往購買<i className="ri-external-link-line text-xs ml-0.5"></i>
          </a>
        </div>
      </div>
    </article>
  );
}
